import {
    HttpErrorResponse,
    HttpInterceptorFn,
    HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
    catchError,
    map,
    switchMap,
    throwError
} from 'rxjs';

import { AuthService } from '@/auth/services';
import { TokenType } from '@/auth/services/auth.service';
import { CryptoService } from '@/service';

const SKIP_REFRESH_URLS = [
    '/auth/login',
    '/auth/refresh',
    '/auth/oauth'
];

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const cryptoService = inject(CryptoService);
    const accessToken = authService.getToken(TokenType.ACCESS);
    const headers: Record<string, string> = {};

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (cryptoService.publicKey) {
        headers['X-Public-Key'] = cryptoService.publicKey;
    }
    const authReq = req.clone({ setHeaders: headers });

    return next(authReq).pipe(
        map(event => {
            if (event instanceof HttpResponse) {
                const aesKeyHeader = event.headers.get('X-Aes-Key');
                if (aesKeyHeader) {
                    cryptoService.setAesKey(aesKeyHeader);
                }

                if (typeof event.body === 'string') {
                    return event.clone({
                        body: decryptBody(event.body, cryptoService)
                    });
                }
            }

            return event;
        }),
        catchError((error: HttpErrorResponse) => {
            const shouldSkipRefresh = SKIP_REFRESH_URLS.some(url =>
                req.url.includes(url)
            );

            if (error.status === 401 && !shouldSkipRefresh) {
                return authService.refreshToken().pipe(
                    switchMap(accessToken => {
                        if (!accessToken) {
                            return throwError(() => error);
                        }

                        const retryHeaders: Record<string, string> = {
                            Authorization: `Bearer ${accessToken}`
                        };

                        if (cryptoService.publicKey) {
                            retryHeaders['X-Public-Key'] = cryptoService.publicKey;
                        }

                        const retryReq = req.clone({
                            setHeaders: retryHeaders
                        });
                        return next(retryReq);
                    }),
                    catchError(refreshError => {
                        authService.logout('unauthorized');
                        return throwError(() => refreshError);
                    })
                );
            }
            return throwError(() => error);
        })
    );
};

function decryptBody(body: string, crypto: CryptoService): any {
    try {
        return JSON.parse(body);
    } catch {
        // not JSON
    }

    return crypto.hasAesKey
        ? crypto.decrypt(body)
        : body;
}
